import React, { useEffect, useId, useRef } from 'react';

/**
 * Shared modal chrome (SRP: layout/overlay only). Content via children.
 * See `modalConventions.js` for when to use this vs `Modal/Modal.jsx`.
 * Escape closes, body scroll locked while open, initial focus inside dialog.
 */
const ModalFrame = ({
  isOpen,
  onClose,
  titleId: titleIdProp,
  title,
  children,
  footer,
  description,
  /** Tailwind max-width utility for the panel (default compact form modals). */
  maxWidthClass = 'max-w-lg'
}) => {
  const autoTitleId = useId();
  const descriptionId = useId();
  const titleId = titleIdProp || autoTitleId;
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return undefined;
    }

    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (!wasOpenRef.current) {
      const node = panelRef.current;
      if (node) {
        const focusable = node.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        (focusable || node).focus?.();
      }
      wasOpenRef.current = true;
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`bg-white rounded-lg shadow-xl ${maxWidthClass} w-full max-h-[90vh] overflow-y-auto outline-none`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {title ? (
            <h2 id={titleId} className="text-xl font-semibold text-gray-900 mb-2">
              {title}
            </h2>
          ) : null}
          {description ? (
            <p id={descriptionId} className="text-sm text-gray-600 mb-4">
              {description}
            </p>
          ) : null}
          {children}
          {footer}
        </div>
      </div>
    </div>
  );
};

export default ModalFrame;
